import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getPerfis, login, getTurnoCaixaAtual } from '../api/client'
import { useUser } from '../App'

// D-95 Fase 3 (2026-05-08): admin pousa no painel; seguranca na saida;
// demais perfis caem em /mesas (operação do dia-a-dia).
// D-102 (2026-05-16): caixa cai em /abrir-caixa se não tiver turno aberto.
// D-160 hotfix (2026-05-25): copa pousa em /copa (KDS read-only).
function landingFor(perfil: string): string {
  if (perfil === 'admin')     return '/admin'
  if (perfil === 'seguranca') return '/saida'
  if (perfil === 'copa')      return '/copa'
  return '/mesas'
}

/**
 * Pós-login: pra perfil 'caixa', consulta turno atual. Se não tem turno aberto,
 * redireciona pra /abrir-caixa. Se já tem, segue pro landing normal.
 * Admin não passa por esse gate (pode operar caixa via /abrir-caixa se quiser,
 * mas o fluxo normal vai pro /admin).
 */
async function postLoginLanding(perfil: string): Promise<string> {
  if (perfil !== 'caixa') return landingFor(perfil)
  try {
    const r = await getTurnoCaixaAtual()
    return r.aberto ? '/mesas' : '/abrir-caixa'
  } catch {
    return '/abrir-caixa'  // fail-open: força abertura se rota está fora do ar
  }
}

export default function LoginPage() {
  const [perfis, setPerfis] = useState<any[]>([])
  const [senhaModal, setSenhaModal] = useState<any | null>(null)
  const [senha, setSenha] = useState('')
  const [error, setError] = useState('')
  const { setUser } = useUser()
  const nav = useNavigate()

  useEffect(() => {
    getPerfis().then(setPerfis).catch(console.error)
  }, [])

  const handleClick = async (p: any) => {
    if (p.perfil === 'caixa' || p.perfil === 'admin') {
      setSenhaModal(p)
      setSenha('')
      setError('')
      return
    }
    try {
      const user = await login(p.id)
      setUser(user)
      nav(await postLoginLanding(user.perfil))
    } catch (e: any) {
      setError(e.message)
    }
  }

  const handleSenha = async () => {
    try {
      const user = await login(senhaModal.id, senha)
      setUser(user)
      setSenhaModal(null)
      nav(await postLoginLanding(user.perfil))
    } catch (e: any) {
      setError(e.message)
    }
  }

  // D-161 hotfix2 (2026-05-26): Sistema QR é usuário fantasma usado pra FK
  // dos lançamentos do QR cliente. Não faz sentido aparecer no login (dono
  // usa Admin pra tudo). Mantido ativo no banco por causa da FK, só escondido
  // da UI de login.
  const NOMES_OCULTOS = new Set(['Sistema QR'])

  // Ordenação determinística do grupo admin: Admin primeiro, Caixa depois.
  const ORDEM_ADMIN = ['Admin', 'Caixa']
  const adminOrdenado = perfis
    .filter(p => (p.perfil === 'caixa' || p.perfil === 'admin') && !NOMES_OCULTOS.has(p.nome))
    .sort((a, b) => {
      const ia = ORDEM_ADMIN.indexOf(a.nome)
      const ib = ORDEM_ADMIN.indexOf(b.nome)
      if (ia === -1 && ib === -1) return 0
      if (ia === -1) return 1
      if (ib === -1) return -1
      return ia - ib
    })

  const grouped = {
    atendentes: perfis.filter(p => p.perfil === 'atendente'),
    admin: adminOrdenado,
    outros: perfis.filter(p => p.perfil === 'seguranca' || p.perfil === 'copa'),
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream via-branco-gelo to-cream-dark/30 flex flex-col items-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center py-8">
          <p className="text-[10px] uppercase tracking-[0.24em] text-terracota/70 font-medium mb-1">Churrascaria</p>
          <h1 className="font-serif text-4xl font-semibold text-vinho">Myro's</h1>
          <p className="text-sm text-gray-500 mt-2">Selecione seu perfil</p>
        </div>

        {error && <div className="bg-red-100 text-red-700 p-2 rounded mb-3 text-sm">{error}</div>}

        <div className="space-y-2">
          <p className="text-xs text-gray-400 uppercase tracking-wide">Atendentes</p>
          <div className="grid grid-cols-2 gap-2">
            {grouped.atendentes.map(p => (
              <button key={p.id} onClick={() => handleClick(p)} className="btn btn-primary text-lg">
                {p.nome}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2 mt-4">
          <p className="text-xs text-gray-400 uppercase tracking-wide">Administração</p>
          <div className="grid grid-cols-2 gap-2">
            {grouped.admin.map(p => (
              <button key={p.id} onClick={() => handleClick(p)} className="btn bg-gray-800 text-white text-lg">
                {p.nome}
              </button>
            ))}
          </div>
        </div>

        {grouped.outros.length > 0 && (
          <div className="space-y-2 mt-4">
            <p className="text-xs text-gray-400 uppercase tracking-wide">Outros</p>
            <div className="grid grid-cols-2 gap-2">
              {grouped.outros.map(p => (
                <button key={p.id} onClick={() => handleClick(p)} className="btn btn-secondary text-lg">
                  {p.nome}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal de senha (D-122: glass) */}
      {senhaModal && (
        <div className="fixed inset-0 bg-carvao/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="glass-modal rounded-2xl p-6 w-full max-w-xs">
            <h2 className="font-serif text-lg font-semibold mb-3 text-carvao">Senha — {senhaModal.nome}</h2>
            {error && <p className="text-red-600 text-sm mb-2">{error}</p>}
            <input
              type="password"
              inputMode="numeric"
              value={senha}
              onChange={e => setSenha(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSenha()}
              className="w-full border rounded p-3 text-2xl text-center tracking-widest mb-3"
              placeholder="****"
              autoFocus
            />
            <div className="flex gap-2">
              <button onClick={() => setSenhaModal(null)} className="btn btn-secondary flex-1">Cancelar</button>
              <button onClick={handleSenha} className="btn btn-primary flex-1">Entrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
